import { useId, type ReactNode } from "react";
import { motion } from "framer-motion";

export interface SegmentItem<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
}

interface SegmentedTabsProps<T extends string> {
  items: SegmentItem<T>[];
  value: T;
  onChange: (next: T) => void;
  className?: string;
}

/**
 * Segmented tabbar — Figma §5.6. Konteyner oq/karta, r16, padding 4.
 * Aktiv segment: soft orange tint fon + primary matn (qattiq emas).
 * Aktiv pill `layoutId` bilan silliq ko'chadi.
 */
export function SegmentedTabs<T extends string>({
  items,
  value,
  onChange,
  className,
}: SegmentedTabsProps<T>) {
  const groupId = useId();
  return (
    <div
      role="tablist"
      className={`flex gap-3 rounded-2xl p-1 ${className ?? ""}`}
      style={{ background: "var(--c-surface)", boxShadow: "inset 0 0 0 1px var(--c-line)" }}
    >
      {items.map((item) => {
        const active = value === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className="relative flex h-9 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-medium transition-colors"
            style={{ color: active ? "var(--primary)" : "var(--c-text)" }}
          >
            {active && (
              <motion.span
                layoutId={`seg-${groupId}`}
                className="absolute inset-0 rounded-xl"
                style={{ background: "var(--c-brand-tint)" }}
                transition={{ type: "spring", damping: 28, stiffness: 360 }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              {item.icon}
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
